import React from 'react';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { Shield, Award, Truck, DollarSign, Users } from 'lucide-react';

interface SQDCMCategory {
  category: string;
  total_workshops: number;
  completed_workshops: number;
  completion_rate: number;
  average_progress: number;
  total_estimated_savings: number;
  total_released_savings: number;
  realization_rate: number;
  performance_score: number;
  improvement_trend: number;
}

interface SQDCMAnalytics {
  safety_performance: SQDCMCategory;
  quality_performance: SQDCMCategory;
  delivery_performance: SQDCMCategory;
  cost_performance: SQDCMCategory;
  morale_performance: SQDCMCategory;
  overall_sqdcm_score: number;
  category_rankings: Array<{
    rank: number;
    category: string;
    performance_score: number;
    completion_rate: number;
    realization_rate: number;
  }>;
}

interface SQDCMRadarChartProps {
  data: SQDCMAnalytics;
  title?: string;
}

const SQDCMRadarChart: React.FC<SQDCMRadarChartProps> = ({ 
  data, 
  title = "SQDCM Performance Analysis" 
}) => {
  const getCategoryIcon = (category: string) => {
    switch (category.toLowerCase()) {
      case 'safety': return Shield;
      case 'quality': return Award;
      case 'delivery': return Truck;
      case 'cost': return DollarSign;
      case 'morale': return Users;
      default: return Award;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category.toLowerCase()) {
      case 'safety': return '#ef4444';
      case 'quality': return '#3b82f6';
      case 'delivery': return '#f59e0b';
      case 'cost': return '#10b981';
      case 'morale': return '#8b5cf6';
      default: return '#6b7280';
    }
  };

  // Prepare data for radar chart
  const radarData = [
    {
      category: 'Safety',
      performance: data.safety_performance.performance_score,
      completion: data.safety_performance.completion_rate,
      realization: data.safety_performance.realization_rate,
      fullMark: 100
    },
    {
      category: 'Quality',
      performance: data.quality_performance.performance_score,
      completion: data.quality_performance.completion_rate,
      realization: data.quality_performance.realization_rate,
      fullMark: 100
    },
    {
      category: 'Delivery',
      performance: data.delivery_performance.performance_score,
      completion: data.delivery_performance.completion_rate,
      realization: data.delivery_performance.realization_rate,
      fullMark: 100
    },
    {
      category: 'Cost',
      performance: data.cost_performance.performance_score,
      completion: data.cost_performance.completion_rate,
      realization: data.cost_performance.realization_rate,
      fullMark: 100
    },
    {
      category: 'Morale',
      performance: data.morale_performance.performance_score,
      completion: data.morale_performance.completion_rate,
      realization: data.morale_performance.realization_rate,
      fullMark: 100
    }
  ];

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-900">{label}</p>
          <div className="space-y-1">
            <p className="text-blue-600">
              Performance: {data.performance.toFixed(1)}%
            </p>
            <p className="text-green-600">
              Completion: {data.completion.toFixed(1)}%
            </p>
            <p className="text-purple-600">
              Realization: {data.realization.toFixed(1)}%
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-blue-500"></div>
          <span className="text-sm text-gray-600">SQDCM Analysis</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Radar Chart */}
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={radarData} margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
              <PolarGrid stroke="#e5e7eb" />
              <PolarAngleAxis dataKey="category" tick={{ fontSize: 12 }} />
              <PolarRadiusAxis 
                angle={90} 
                domain={[0, 100]} 
                tick={{ fontSize: 10 }}
                tickCount={5}
              />
              <Tooltip content={<CustomTooltip />} />
              <Radar
                name="Performance"
                dataKey="performance"
                stroke="#3b82f6"
                fill="#3b82f6"
                fillOpacity={0.3}
                strokeWidth={2}
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        {/* Category Details */}
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-3">Overall SQDCM Score</h4>
            <div className="flex items-center gap-3">
              <div className="text-3xl font-bold text-blue-600">
                {data.overall_sqdcm_score.toFixed(1)}
              </div>
              <div className="flex-1">
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className="bg-blue-500 h-3 rounded-full transition-all duration-300"
                    style={{ width: `${data.overall_sqdcm_score}%` }}
                  ></div>
                </div>
                <p className="text-sm text-gray-600 mt-1">Out of 100</p>
              </div>
            </div>
          </div>

          {/* Category Rankings */}
          <div>
            <h4 className="font-semibold text-gray-900 mb-3">Category Rankings</h4>
            <div className="space-y-2">
              {data.category_rankings.map((ranking) => {
                const IconComponent = getCategoryIcon(ranking.category);
                const color = getCategoryColor(ranking.category);
                
                return (
                  <div key={ranking.category} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-center w-8 h-8 bg-white rounded-full border-2 border-gray-200">
                      <span className="text-sm font-semibold text-gray-600">#{ranking.rank}</span>
                    </div>
                    <IconComponent className="w-5 h-5" style={{ color }} />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{ranking.category}</p>
                      <p className="text-sm text-gray-600">
                        Score: {ranking.performance_score.toFixed(1)}% | 
                        Completion: {ranking.completion_rate.toFixed(1)}% | 
                        Realization: {ranking.realization_rate.toFixed(1)}%
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Category Performance */}
      <div className="mt-6">
        <h4 className="font-semibold text-gray-900 mb-4">Detailed Category Performance</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries({
            'Safety': data.safety_performance,
            'Quality': data.quality_performance,
            'Delivery': data.delivery_performance,
            'Cost': data.cost_performance,
            'Morale': data.morale_performance
          }).map(([category, performance]) => {
            const IconComponent = getCategoryIcon(category);
            const color = getCategoryColor(category);
            
            return (
              <div key={category} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <IconComponent className="w-5 h-5" style={{ color }} />
                  <h5 className="font-semibold text-gray-900">{category}</h5>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Workshops</span>
                    <span className="text-sm font-semibold">{performance.total_workshops}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Completion</span>
                    <span className="text-sm font-semibold text-green-600">
                      {performance.completion_rate.toFixed(1)}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Progress</span>
                    <span className="text-sm font-semibold text-blue-600">
                      {performance.average_progress.toFixed(1)}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Realization</span>
                    <span className="text-sm font-semibold text-purple-600">
                      {performance.realization_rate.toFixed(1)}%
                    </span>
                  </div>
                  <div className="mt-3">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm text-gray-600">Performance Score</span>
                      <span className="text-sm font-semibold" style={{ color }}>
                        {performance.performance_score.toFixed(1)}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="h-2 rounded-full transition-all duration-300"
                        style={{ 
                          width: `${performance.performance_score}%`,
                          backgroundColor: color
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SQDCMRadarChart;


